/* This example requires Tailwind CSS v2.0+ */
import React, { useEffect, useState, useRef } from "react"
import Image from "next/image"
import Link from "next/link"

import { Tab } from "@headlessui/react"
import { classNames } from "core/utils"
import { LoadingDots } from "@components/ui"

import { ProductOption, Product } from "@lib/swell/storefront-data-hooks/src/types"
import {
  ArrowRightCircleIcon,
  ArrowLeftCircleIcon,
  HomeIcon,
  ShoppingCartIcon,
} from "@heroicons/react/24/outline"
import { useUI } from "@components/ui/context"
import { NextSeo } from "next-seo"
import ConfiguratorProductForm from "@components/ConfiguratorProductForm/ConfiguratorProductForm"
import { StepProduct, useConfigurator } from "../ConfiguratorContext"
import LoadingOverlay from "../LoadingOverlay"
import { ConfiguratorEstimateModal } from "../ConfiguratorEstimateModal"
import ImageGallery from "./ImageGallery"

interface NavigatePrevStepButtonProps {
  loadingConfigurator?: boolean
  goToPreviousStep: () => void
  prevButtonIsEnabled: boolean
}
function NavigatePrevStepButton({
  loadingConfigurator,
  goToPreviousStep,
  prevButtonIsEnabled,
}: NavigatePrevStepButtonProps) {
  return (
    <div>
      <button
        type="button"
        disabled={loadingConfigurator || !prevButtonIsEnabled}
        onClick={goToPreviousStep}
        className={classNames(
          prevButtonIsEnabled
            ? "inline-flex items-center gap-x-1.5 rounded-2xl bg-primary px-3 py-2 text-sm font-semibold shadow-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary bg-white border border-black"
            : "hidden"
        )}
      >
        <ArrowLeftCircleIcon className="-mr-0.5 h-5 w-5" aria-hidden="true" />
        Prev
      </button>
    </div>
  )
}

interface NavigateNextStepButtonProps {
  loadingConfigurator?: boolean
  goToNextStep: () => void
  nextButtonIsEnabled: boolean
  loadEstimateForm: () => void
}
function NavigateNextStepButton({
  loadingConfigurator,
  goToNextStep,
  nextButtonIsEnabled,
  loadEstimateForm,
}: NavigateNextStepButtonProps) {
  return (
    <div>
      <button
        type="button"
        disabled={loadingConfigurator || !nextButtonIsEnabled}
        onClick={goToNextStep}
        className={classNames(
          nextButtonIsEnabled
            ? "inline-flex items-center gap-x-1.5 rounded-2xl bg-primary px-3 py-2 text-sm font-semibold shadow-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary bg-white border border-black"
            : "hidden"
        )}
      >
        Next
        <ArrowRightCircleIcon className="-mr-0.5 h-5 w-5" aria-hidden="true" />
      </button>
      <button
        type="button"
        disabled={loadingConfigurator || nextButtonIsEnabled}
        onClick={loadEstimateForm}
        className={classNames(
          nextButtonIsEnabled
            ? "hidden"
            : "inline-flex items-center gap-x-1.5 rounded-2xl bg-primary px-3 py-2 text-sm font-semibold shadow-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary bg-white border border-black"
        )}
      >
        Finish
        <ShoppingCartIcon className="-mr-0.5 h-5 w-5" aria-hidden="true" />
      </button>
    </div>
  )
}

export type TabbedProductOption = {
  title: string
  option: ProductOption
  name: string
}
export interface ConfiguratorProps {
  className?: string
  children?: any
  product: Product
  renderSeo?: boolean
  description?: string
  title?: string
}

export default function Configurator({
  product,
  renderSeo = true,
  description = product.description,
  title = product.name,
}: {
  product: any
  renderSeo?: boolean
  description?: string
  title?: string
}) {
  const {
    currentStep,
    steps,
    setConfiguratorProduct,
    goToPreviousStep,
    goToNextStep,
    updateCurrentStepVariant,
    updateCurrentStepSelections,
    currentFormattedTotalPrice,
    loadingConfigurator,
    currentStepIndex,
    setCurrentStepIndex,
    prevButtonIsEnabled,
    nextButtonIsEnabled,
    loadEstimateForm,
    configuratorEstimateModalIsOpen,
    setConfiguratorEstimateModalIsOpen,
  } = useConfigurator()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { openSidebar } = useUI()
  const { navigationLinks, logo } = useUI()
  const allProductImages =
    currentStep?.stepProducts?.[0]?.product?.images?.map((image: any) => ({ ...image })) || []
  const allVariantImages =
    currentStep?.stepProducts?.[0]?.variant?.images?.map((image: any) => ({
      ...image,
      src: image.file?.url,
    })) || []
  const allImages = [...allVariantImages, ...allProductImages]
  const uniqueImages = [...new Map(allImages.map((m) => [m.src, m])).values()]
  const sidebarRef = useRef<HTMLDivElement>(null)

  function handleGoToNextStep() {
    goToNextStep()
    sidebarRef.current?.scrollTo(0, 0)
    window.scrollTo(0, 0)
  }
  function handleGoToPreviousStep() {
    goToPreviousStep()
    sidebarRef.current?.scrollTo(0, 0)
    window.scrollTo(0, 0)
  }

  function handleTabChange(index: number) {
    setCurrentStepIndex(index)
    sidebarRef.current?.scrollTo(0, 0)
    window.scrollTo(0, 0)
  }

  useEffect(() => {
    setConfiguratorProduct(product)
  }, [product])

  return (
    <React.Fragment>
      {renderSeo && (
        <NextSeo
          title={title}
          description={description}
          openGraph={{
            type: "website",
            title: title,
            description: description,
            images: [
              {
                url: product.images?.[0]?.file.url!,
                width: 800,
                height: 600,
                alt: title,
              },
            ],
          }}
        />
      )}

      {/*
        This example requires updating your template:

        ```
        <html class="h-full bg-gray-100">
        <body class="h-full">
        ```
      */}
      <div className="relative flex flex-col h-screen bg-gray-800">
        {/* Configurator */}
        {<LoadingOverlay show={loadingConfigurator} />}
        <div className="lg:fixed lg:inset-0">
          {/* ConfiguratorNav Header */}
          <nav className="bg-gray-800 px-2 sm:px-4 py-2 z-40 sticky sm:relative top-0">
            <div className="hidden sm:flex flex-col sm:flex-row items-center border-b border-white pb-4">
              {/* Home Button */}
              <div className="sm:basis-1/3 flex rounded-full">
                {logo && logo.image && (
                  <Link href="/">
                    <img className="h-6 w-auto" src={logo.image} alt="New Way Van Life" />
                  </Link>
                )}
              </div>

              {/* Title */}
              <div className="sm:basis-1/3 flex grow flex-col space-y-4 justify-center">
                <h1 className="text-white text-lg lg:text-2xl py-1 text-center text-nowrap">
                  <div className="flex-col items-center">
                    <h1 className="text-lg sm:text-3xl text-white text-center">{title}</h1>
                    {/* <h2 className="text-lg text-white text-center">{currentStep && currentStep.name}</h2> */}
                  </div>
                </h1>
              </div>

              {/* Total Price */}
              <div className="flex sm:basis-1/3 justify-end sm:w-36 lg:w-48 ">
                <div className="flex-row sm:flex-col items-center">
                  <div className="text-md sm:text-2xl text-white font-bold text-center">
                    ESTIMATE
                  </div>
                  <div aria-label="price" className="text-xl lg:text-5xl font-normal text-white">
                    {currentFormattedTotalPrice()}
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Header */}
            <div className="sm:hidden flex flex-col items-center border-b border-white pb-2">
              {/* Home Button */}
              <div className="flex rounded-full space-x-4 items-center">
                {logo && logo.image && (
                  <Link href="/">
                    <img
                      className="h-6 w-auto object-contain"
                      src={logo.image}
                      alt="New Way Van Life"
                    />
                  </Link>
                )}
                <h1 className="text-lg sm:text-3xl text-white text-center">{title}</h1>
              </div>

              {/* Total Price */}
              <div className="flex space-x-4 items-center mt-2">
                <div aria-label="price" className="text-lg font-normal text-white">
                  <span className="text-md text-white font-bold text-center">ESTIMATE: </span>
                  {currentFormattedTotalPrice()}
                </div>
              </div>
            </div>

            {/* Prev/Next Buttons and Step title */}
            <div className="flex mt-3">
              <div className="basis-1/3">
                <NavigatePrevStepButton
                  loadingConfigurator={loadingConfigurator}
                  goToPreviousStep={handleGoToPreviousStep}
                  prevButtonIsEnabled={prevButtonIsEnabled}
                />
              </div>
              <div className="basis-1/3">
                <Tab.Group selectedIndex={currentStepIndex} onChange={handleTabChange}>
                  <div className="flex text-md grow items-center justify-center">
                    <Tab.List className="flex grow space-x-1 rounded-2xl bg-blue-900/20 p-1">
                      {steps?.map((step, index) => (
                        <Tab
                          key={step.id}
                          disabled={step.disabled}
                          className={({ selected }) =>
                            classNames(
                              "w-full rounded-2xl py-1 lg:px-8 lg:px-6 sm:px-4 text-sm lg:text-md font-medium leading-5 text-primary",
                              "ring-white ring-opacity-60 ring-offset-2 ring-offset-primary focus:outline-hidden focus:ring-2",
                              selected
                                ? "bg-white shadow-sm"
                                : step.disabled
                                ? "text-slate-500"
                                : "text-white hover:bg-white/[0.12] hover:text-white"
                            )
                          }
                        >
                          <span className="sm:hidden">{index + 1}</span>
                          <span className="hidden sm:block">{step.name}</span>
                        </Tab>
                      ))}
                    </Tab.List>
                  </div>
                </Tab.Group>
              </div>
              <div className="basis-1/3 flex justify-end">
                <NavigateNextStepButton
                  loadingConfigurator={loadingConfigurator}
                  goToNextStep={handleGoToNextStep}
                  nextButtonIsEnabled={nextButtonIsEnabled}
                  loadEstimateForm={loadEstimateForm}
                />
              </div>
            </div>
          </nav>

          <div className="flex flex-col-reverse lg:flex-row flex-1 overflow-hidden">
            {/* Left Column - Current Step  */}
            <div className="flex flex-col lg:h-screen lg:w-120 lg:z-40">
              {/* Sidebar component */}
              <div className="flex min-h-0 flex-1 flex-col bg-gray-50">
                <div ref={sidebarRef} className="flex flex-1 flex-col overflow-y-auto pb-4">
                  <div className="px-2 lg:mb-48">
                    {loadingConfigurator ? (
                      <LoadingDots />
                    ) : (
                      <Tab.Group selectedIndex={currentStepIndex} onChange={setCurrentStepIndex}>
                        <Tab.Panels>
                          <Tab.Panel
                            key={currentStep && currentStep.id}
                            className={classNames(
                              "space-y-4 rounded-xl p-3",
                              "ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-hidden focus:ring-2"
                            )}
                          >
                            {currentStep?.stepProducts?.map(
                              (stepProduct: StepProduct, index: number) => {
                                if (stepProduct?.product && stepProduct?.swellProduct) {
                                  return (
                                    <div key={`step-product-${index}`}>
                                      <div className="px-2 text-sm tracking-tight text-gray-900 lg:text-base lg:font-extrabold">
                                        {stepProduct.product.name}
                                      </div>
                                      <ConfiguratorProductForm
                                        product={stepProduct.product}
                                        swellProduct={stepProduct.swellProduct}
                                        variant={stepProduct.variant}
                                        setVariant={(variant) =>
                                          updateCurrentStepVariant(stepProduct.product!.id, variant)
                                        }
                                        hideAddToCart={true}
                                        currentSelections={stepProduct.selections}
                                        setCurrentSelections={(selections) =>
                                          updateCurrentStepSelections(
                                            stepProduct.product!.id,
                                            selections
                                          )
                                        }
                                      />
                                    </div>
                                  )
                                } else {
                                  return null
                                }
                              }
                            )}

                            {/* Prev/Next Buttons */}
                            <div className="flex pt-2">
                              <div className="basis-1/3">
                                <NavigatePrevStepButton
                                  loadingConfigurator={loadingConfigurator}
                                  goToPreviousStep={handleGoToPreviousStep}
                                  prevButtonIsEnabled={prevButtonIsEnabled}
                                />
                              </div>
                              <div className="basis-1/3 grow"></div>
                              <div className="basis-1/3 flex justify-end">
                                <NavigateNextStepButton
                                  loadingConfigurator={loadingConfigurator}
                                  goToNextStep={handleGoToNextStep}
                                  nextButtonIsEnabled={nextButtonIsEnabled}
                                  loadEstimateForm={loadEstimateForm}
                                />
                              </div>
                            </div>
                          </Tab.Panel>
                        </Tab.Panels>
                      </Tab.Group>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Associated Step Images */}
            <main className="w-full h-full bg-slate-400 lg:h-svh lg:-mt-16">
              <ImageGallery uniqueImages={uniqueImages} />
            </main>
          </div>
        </div>
      </div>

      <ConfiguratorEstimateModal
        configuratorEstimateModalIsOpen={configuratorEstimateModalIsOpen}
        setConfiguratorEstimateModalIsOpen={setConfiguratorEstimateModalIsOpen}
        currentFormattedTotalPrice={currentFormattedTotalPrice}
        title={title}
        steps={steps}
      />
    </React.Fragment>
  )
}
